import { useEffect, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRoute } from 'wouter';
import { api, ApiError } from '../lib/api';
import { useUpdateBrief, type Project } from '../lib/projects';
import { ProjectHeader } from '../components/ProjectHeader';

export function SettingsPage() {
  const [, params] = useRoute('/projects/:id/settings');
  const id = params?.id;
  const project = useQuery({
    enabled: !!id,
    queryKey: ['projects', id],
    queryFn: () => api<{ project: Project }>(`/api/projects/${id}`).then((r) => r.project),
  });

  if (!id) return null;

  return (
    <div className="min-h-full">
      <ProjectHeader projectId={id} page="SETTINGS" />
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-4 md:py-8 space-y-8">
        <header>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted">Brand controls</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-1">
            Operating rules for this brand
          </h1>
        </header>

        <section className="grid md:grid-cols-3 gap-4">
          <SettingCard
            label="Channels"
            value={project.data?.channels.join(' + ') || '—'}
            helper="Configured in Strategy."
          />
          <SettingCard
            label="Monthly spend cap"
            value={`$${project.data?.monthlyCostCeilingUsd ?? '—'}`}
            helper="Caps what your team can spend each month."
          />
          <SettingCard
            label="Status"
            value={project.data?.status ?? '—'}
            helper="Active brands run when you brief them."
          />
        </section>

        {project.data && <RenameBrandForm project={project.data} />}
        {project.data && <HelpfulnessRatioForm project={project.data} />}
        {project.data && <BrandRulesForm project={project.data} />}
      </main>
    </div>
  );
}

function RenameBrandForm({ project }: { project: Project }) {
  const update = useUpdateBrief(project.id);
  const [name, setName] = useState(project.name);
  const [saved, setSaved] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => { setName(project.name); setSaved(false); setErrorMessage(null); }, [project.id, project.name]);

  const trimmed = name.trim();
  const dirty = trimmed !== project.name;
  const valid = trimmed.length >= 2 && trimmed.length <= 80;

  const onSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!dirty || !valid) return;
    setSaved(false);
    setErrorMessage(null);
    try {
      await update.mutateAsync({ name: trimmed });
      setSaved(true);
    } catch (err) {
      setErrorMessage(err instanceof ApiError ? err.message : 'Couldn’t save right now. Try again in a moment.');
    }
  };

  return (
    <section className="rounded-xl border border-border-color bg-surface p-4 md:p-6">
      <header className="mb-4">
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted">Brand identity</p>
        <h2 className="font-display text-xl font-bold mt-1">Brand name</h2>
        <p className="text-xs text-muted mt-1 leading-relaxed">
          What this brand is called inside Bubbles. The URL slug stays as it was — only the display name changes.
        </p>
      </header>
      <form onSubmit={onSave} className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]">
          <label htmlFor="brand-name" className="font-mono text-[10px] uppercase tracking-wider text-muted block mb-1.5">
            Name
          </label>
          <input
            id="brand-name"
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setSaved(false); setErrorMessage(null); }}
            maxLength={80}
            className="w-full bg-surface-2 border border-border-color rounded px-3 py-2 text-sm font-display text-text-primary focus:outline-none focus:border-accent-cyan transition-colors"
          />
        </div>
        <button
          type="submit"
          disabled={!dirty || !valid || update.isPending}
          className="font-mono text-[11px] uppercase tracking-wider px-4 py-2 rounded bg-accent-cyan/15 text-accent-cyan border border-accent-cyan/40 hover:bg-accent-cyan/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {update.isPending ? 'Saving…' : 'Save name'}
        </button>
        {saved && !dirty && <span className="font-mono text-[10px] uppercase tracking-wider text-accent-emerald">Saved</span>}
        {errorMessage && <span className="font-mono text-[10px] uppercase tracking-wider text-accent-red">{errorMessage}</span>}
      </form>
    </section>
  );
}

function HelpfulnessRatioForm({ project }: { project: Project }) {
  const update = useUpdateBrief(project.id);
  const [utility, setUtility] = useState(project.helpfulnessRatioUtility);
  const [promo, setPromo] = useState(project.helpfulnessRatioPromo);
  const [saved, setSaved] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setUtility(project.helpfulnessRatioUtility);
    setPromo(project.helpfulnessRatioPromo);
    setSaved(false);
    setErrorMessage(null);
  }, [project.id, project.helpfulnessRatioUtility, project.helpfulnessRatioPromo]);

  const dirty = utility !== project.helpfulnessRatioUtility || promo !== project.helpfulnessRatioPromo;
  const valid = utility >= 1 && utility <= 99 && promo >= 1 && promo <= 99;

  const onSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!dirty || !valid) return;
    setSaved(false);
    setErrorMessage(null);
    try {
      await update.mutateAsync({
        helpfulnessRatioUtility: utility,
        helpfulnessRatioPromo: promo,
      });
      setSaved(true);
    } catch (err) {
      setErrorMessage(err instanceof ApiError ? err.message : 'Couldn’t save right now. Try again.');
    }
  };

  return (
    <section className="rounded-xl border border-border-color bg-surface p-4 md:p-6">
      <header className="mb-4">
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted">Helpfulness ratio</p>
        <h2 className="font-display text-xl font-bold mt-1">Utility to promo cadence</h2>
        <p className="text-xs text-muted mt-1 leading-relaxed">
          Your brand safety officer enforces this ratio over a rolling 30-day window. The strategy team proposed these values from the brief; you can override.
        </p>
      </header>
      <form onSubmit={onSave} className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="ratio-utility" className="font-mono text-[10px] uppercase tracking-wider text-muted block mb-1.5">
            Utility posts
          </label>
          <input
            id="ratio-utility"
            type="number"
            min={1}
            max={99}
            value={utility}
            onChange={(e) => { setUtility(parseInt(e.target.value, 10) || 1); setSaved(false); setErrorMessage(null); }}
            className="w-24 bg-surface-2 border border-border-color rounded px-3 py-2 text-sm font-mono text-text-primary focus:outline-none focus:border-accent-cyan transition-colors"
          />
        </div>
        <span className="font-mono text-2xl text-muted pb-2">:</span>
        <div>
          <label htmlFor="ratio-promo" className="font-mono text-[10px] uppercase tracking-wider text-muted block mb-1.5">
            Promo posts
          </label>
          <input
            id="ratio-promo"
            type="number"
            min={1}
            max={99}
            value={promo}
            onChange={(e) => { setPromo(parseInt(e.target.value, 10) || 1); setSaved(false); setErrorMessage(null); }}
            className="w-24 bg-surface-2 border border-border-color rounded px-3 py-2 text-sm font-mono text-text-primary focus:outline-none focus:border-accent-cyan transition-colors"
          />
        </div>
        <button
          type="submit"
          disabled={!dirty || !valid || update.isPending}
          className="font-mono text-[11px] uppercase tracking-wider px-4 py-2 rounded bg-accent-cyan/15 text-accent-cyan border border-accent-cyan/40 hover:bg-accent-cyan/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {update.isPending ? 'Saving…' : 'Save ratio'}
        </button>
        {saved && !dirty && <span className="font-mono text-[10px] uppercase tracking-wider text-accent-emerald">Saved</span>}
        {errorMessage && <span className="font-mono text-[10px] uppercase tracking-wider text-accent-red">{errorMessage}</span>}
      </form>
    </section>
  );
}

function SettingCard({ label, value, helper }: { label: string; value: string; helper: string }) {
  return (
    <div className="rounded-xl border border-border-color bg-surface p-5">
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted">{label}</p>
      <p className="mt-2 font-display text-2xl font-bold capitalize">{value}</p>
      <p className="mt-2 text-xs text-muted leading-relaxed">{helper}</p>
    </div>
  );
}

function BrandRulesForm({ project }: { project: Project }) {
  const update = useUpdateBrief(project.id);
  const [bannedText, setBannedText] = useState((project.bannedPhrases ?? []).join('\n'));
  const [voiceText, setVoiceText] = useState((project.voiceRules ?? []).join('\n'));
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setBannedText((project.bannedPhrases ?? []).join('\n'));
    setVoiceText((project.voiceRules ?? []).join('\n'));
  }, [project.id, project.bannedPhrases.join('|'), project.voiceRules.join('|')]);

  const banned = parseLines(bannedText);
  const rules = parseLines(voiceText);

  const dirty =
    banned.join('|') !== (project.bannedPhrases ?? []).join('|') ||
    rules.join('|') !== (project.voiceRules ?? []).join('|');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    await update.mutateAsync({ bannedPhrases: banned, voiceRules: rules });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  const errorMessage = update.error instanceof ApiError ? update.error.message : null;

  return (
    <section className="rounded-xl border border-border-color bg-surface p-4 md:p-6 space-y-6">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-wider text-accent-cyan">Voice enforcement</p>
        <h2 className="font-display text-xl font-bold mt-1">Banned phrases & voice rules</h2>
        <p className="mt-2 text-sm text-muted leading-relaxed max-w-2xl">
          The Writer reads these as hard constraints. Empathy Critic and Brand Safety reject any draft that violates them.
          One entry per line. Case-insensitive substring match for banned phrases. Voice rules pass through verbatim to the
          agents.
        </p>
      </header>

      <form onSubmit={onSubmit} className="grid md:grid-cols-2 gap-6">
        <div>
          <label
            htmlFor="banned"
            className="block font-mono text-[10px] uppercase tracking-wider text-muted mb-2"
          >
            Banned phrases ({banned.length})
          </label>
          <textarea
            id="banned"
            value={bannedText}
            onChange={(e) => setBannedText(e.target.value)}
            rows={10}
            placeholder={'optimise\nleverage\nyour next opportunity is waiting\nutilise\nlet us simplify'}
            className="w-full bg-background-dark/60 border border-border-color rounded-md px-3 py-2.5 text-sm font-mono leading-relaxed text-text-primary placeholder-muted focus:outline-none focus:border-accent-cyan transition-colors"
          />
          <p className="mt-1 text-[10px] font-mono uppercase tracking-wider text-muted">
            One phrase per line. Match is case-insensitive substring.
          </p>
        </div>

        <div>
          <label
            htmlFor="rules"
            className="block font-mono text-[10px] uppercase tracking-wider text-muted mb-2"
          >
            Voice rules ({rules.length})
          </label>
          <textarea
            id="rules"
            value={voiceText}
            onChange={(e) => setVoiceText(e.target.value)}
            rows={10}
            placeholder={'Plainspoken, slightly literary\nNo em-dashes between sentences\nAustralian/British English\nNo motivational poster lines\nNo corporate-consultant register'}
            className="w-full bg-background-dark/60 border border-border-color rounded-md px-3 py-2.5 text-sm font-mono leading-relaxed text-text-primary placeholder-muted focus:outline-none focus:border-accent-cyan transition-colors"
          />
          <p className="mt-1 text-[10px] font-mono uppercase tracking-wider text-muted">
            One rule per line. Passed verbatim to Writer / Editor / Empathy.
          </p>
        </div>

        <div className="md:col-span-2 flex items-center gap-3 border-t border-border-color pt-4">
          <button
            type="submit"
            disabled={!dirty || update.isPending}
            className="btn-bracket bg-primary text-white px-4 py-2 hover:bg-primary/90 disabled:opacity-40 transition-colors"
          >
            {update.isPending ? 'SAVING' : 'SAVE_RULES'}
          </button>
          {saved && (
            <span className="font-mono text-[10px] uppercase tracking-wider text-accent-emerald">SAVED</span>
          )}
          {errorMessage && (
            <span className="font-mono text-[10px] uppercase tracking-wider text-accent-red">
              [ERROR] {errorMessage}
            </span>
          )}
          {!dirty && !saved && !errorMessage && (
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted">UP_TO_DATE</span>
          )}
        </div>
      </form>
    </section>
  );
}

function parseLines(text: string): string[] {
  return Array.from(
    new Set(
      text
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0),
    ),
  );
}
